import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { useApi } from "../../api/context";
import type { UsageMeterInfo } from "../../api/types";

const LABEL = {
  contacts: "billing.account.usage.contacts",
  messages: "billing.account.usage.messages",
  aiTokens: "billing.account.usage.aiTokens",
} as const;

/** Usage as a percentage of the plan limit (0 when unlimited), capped at 100. */
export function meterPercent(meter: Pick<UsageMeterInfo, "used" | "limit">): number {
  if (meter.limit === null || meter.limit <= 0) return 0;
  return Math.min(100, Math.round((meter.used / meter.limit) * 100));
}

/**
 * WA-70: top-bar usage meters (contacts, messages this month, AI tokens vs plan limits) that
 * link to Settings → Billing. A failed load hides the strip rather than showing wrong numbers.
 */
export function UsageMeters({ workspaceId }: { workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const [meters, setMeters] = useState<readonly UsageMeterInfo[] | null>(null);

  useEffect(() => {
    let active = true;
    api.getUsageMeters(workspaceId).then(
      (usage) => {
        if (active) setMeters(usage.meters);
      },
      () => {
        if (active) setMeters(null);
      },
    );
    return () => {
      active = false;
    };
  }, [api, workspaceId]);

  if (!meters || meters.length === 0) return null;
  const number = new Intl.NumberFormat(locale === "ar" ? "ar-SA-u-nu-latn" : "en-US");
  return (
    <Link
      to={`/w/${workspaceId}/settings/billing`}
      aria-label={t("billing.account.usage.ariaLabel")}
      className="inline-flex min-h-8 w-full max-w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-line bg-surface-muted px-3 py-1.5 text-xs text-fg hover:bg-surface-sunken"
      data-testid="usage-meters"
    >
      {meters.map((m) => {
        const pct = meterPercent(m);
        const text =
          m.limit === null
            ? t("billing.account.usage.unlimited", { used: number.format(m.used) })
            : t("billing.account.usage.of", {
                used: number.format(m.used),
                limit: number.format(m.limit),
              });
        return (
          <span
            key={m.key}
            className="inline-flex items-center gap-1 whitespace-nowrap"
            data-meter={m.key}
          >
            <span className="text-fg-muted">{t(LABEL[m.key])}</span>
            <bdi className="tabular-nums">{text}</bdi>
            {m.limit !== null && (
              <span
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                className="h-1 w-8 overflow-hidden rounded-full bg-line"
              >
                <span
                  className={`block h-full ${pct >= 90 ? "bg-action-danger" : "bg-action-primary"}`}
                  style={{ inlineSize: `${String(pct)}%` }}
                />
              </span>
            )}
          </span>
        );
      })}
    </Link>
  );
}
